import './JTBDexercise.scss';

const sectionIntro = {
  title: 'Vamos aplicar ao seu Negócio',
  subtitle:
    'Identifique os Jobs To Be Done dos seus clientes, e descubra oportunidades para tornar a sua oferta mais relevante e valiosa, em apenas três passos.',
};

const step1 = {
  title: 'Passo 1.',
  highlight: 'Identifique os Jobs funcionais, sociais e emocionais',
  subtitle:
    'Pense nos seus clientes e no que realmente procuram quando escolhem o seu produto ou serviço. Reflita sobre as seguintes questões para identificar os Jobs funcionais, sociais e emocionais.',
  cards: [
    {
      title: 'Job Funcional',
      icon: '/images/jtbdpage/functional.svg',
      description:
        'Qual é a função essencial que o meu produto/serviço desempenha para o cliente?',
      example:
        'Uma lavandaria oferece a conveniência de lavar e secar roupas sem esforço.',
    },
    {
      title: 'Job Social',
      icon: '/images/jtbdpage/social.svg',
      description:
        'Que imagem é que os meus clientes gostariam de projetar ao utilizar o meu produto/serviço?',
      example:
        'Um restaurante saudável faz os clientes parecerem mais conscientes da saúde e do bem-estar.',
    },
    {
      title: 'Job Emocional',
      icon: '/images/jtbdpage/emotional.svg',
      description:
        'Que emoção procuram os meus clientes ao usar o meu produto/serviço?',
      example:
        'Comprar um bolo de aniversário proporciona ao cliente um sentimento de alegria e satisfação por fazer alguém feliz.',
    },
  ],
};

const step2 = {
  title: 'Passo 2.',
  highlight: 'Identifique Jobs Emocionais Ocultos',
  subtitle: [
    'Agora que definiu os principais Jobs, vamos procurar Jobs ocultos. Aqueles que não são imediatamente óbvios, mas que desempenham um papel fundamental na decisão do cliente.',
    'Escolha um dos Jobs do passo anterior e pergunte Porquê até 5 vezes, para descobrir a motivação profunda do cliente.',
  ],
  example: {
    title: 'Situação inicial:',
    description: 'O cliente compra um cappuccino às 8h30m da manhã.',
    whySteps: [
      'Porque quer beber café antes de começar o dia.',
      'Porque o café ajuda-o a sentir-se mais desperto e concentrado.',
      'Porque precisa de estar focado para enfrentar um dia cheio de reuniões e decisões importantes.',
      'Porque quer começar o dia de forma organizada, produtiva e sem stress.',
      'Porque lhe dá uma sensação de controlo, bem-estar e confiança para enfrentar o dia.',
    ],
    cardtitle:'O que podemos retirar desta análise?',
    summary:
      'A empresa que vende o cappuccino não está apenas a vender uma bebida. Está a oferecer um ritual diário que ajuda o cliente a centrar-se, respirar fundo e começar o dia com confiança.',
    jobs: [
      {
        icon: '/images/jtbdpage/functional-orange.svg',
        title: 'Job to be Done funcional',
        description: 'Beber café para acordar e focar',
      },
      {
        icon: '/images/jtbdpage/emotional-orange.svg',
        title: 'Job emocional / Oculto',
        description:
          'Sentir-se em controlo do dia e preparado para enfrentar responsabilidades.',
      },
    ],
  },
};

const step3 = {
  title: 'Passo 3.',
  highlight: 'Transforme o que aprendeu numa Proposta de Valor clara e apelativa',
  subtitle:
    'Com base nos Jobs identificados, pode ajustar a forma como apresenta a sua oferta — desde o ambiente da loja até à comunicação e produtos complementares. O objetivo é alinhar-se com o que o cliente realmente procura sentir.',
  note: {
    image: '/images/jtbdpage/notes.svg',
    label: 'Perguntas úteis para orientar a sua proposta de valor:',
    tips: [
      'Melhore a eficácia do Job funcional.',
      'Reforce o valor social na sua comunicação.',
      'Crie experiências que ativem a emoção desejada.',
    ],
  },
  example: {
    title: 'Exemplo:',
    context:
      'Se o cliente procura mais do que apenas cafeína — procura começar o dia com uma sensação de equilíbrio e controlo — pode ajustar a proposta de valor da seguinte forma:',
    quote: 'Mais do que café - um momento de equilíbrio antes de tudo começar.',
  },
};

const JTBDexercise = () => {
  return (
    <>
      {/* Section 0 – Intro */}
      <section className="JTBDexercise section">
        <div className="wrapper wrapper--small">
          <h2 className="wrapper__title wrapper centered">{sectionIntro.title}</h2>
          <div className="wrapper__subtitle h6 centered">{sectionIntro.subtitle}</div>
        </div>
      </section>

      {/* Section 1 – Step 1 */}
      <section className="JTBDPart1 section">
        <div className="wrapper wrapper--large">
          <h3 className="wrapper__title wrapper centered">
            <span className="title-highlight">{step1.title}</span> {step1.highlight}
          </h3>
          <div className="wrapper__subtitle h6 centered">{step1.subtitle}</div>

          <div className="row col-4">
            {step1.cards.map((card, index) => (
              <div key={index} className="col card">
                <img alt="" className="card__icon" src={card.icon} />
                <h3 className="card__title h4">{card.title}</h3>
                <div className="card__description centered">
                  {card.description}
                  <p>
                    <span className="card__highlight">Exemplo:</span> {card.example}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2 – Step 2 */}
      <section className="JTBDPart2 section">
        <div className="wrapper wrapper--medium">
          <h3 className="wrapper__title wrapper centered">
            <span className="title-highlight">{step2.title}</span> {step2.highlight}
          </h3>
          <div className="wrapper__subtitle centered">
            {step2.subtitle.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>

          <div className="row">
            <div className="col">
              <div className="card">
                <p className="card__title h4">{step2.example.title}</p>
                <p className="card__description">{step2.example.description}</p>
                <ol className="card__list">
                  {step2.example.whySteps.map((step, i) => (
                    <li key={i}>
                      <span className="card__list--question">Porquê?</span>
                      <span className="card__list--answer" data-icon="✔">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="col">
              <div className="card extra-padding">
                {step2.example.jobs.map((job, i) => (
                  <div className="card__title--img" key={i}>
                    <img alt="" className="card__icon" src={job.icon} />
                    <div>
                      <div className="card__title h4">{job.title}</div>
                      <div className="card__description h6">{job.description}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="card margin-top">
                <p className="card__title h4">{step2.example.cardtitle}</p>
                <p className="card__description">{step2.example.summary}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 – Step 3 */}
      <section className="JTBDPart3 section">
        <div className="wrapper wrapper--large">
          <h3 className="wrapper__title centered">
            <span className="title-highlight">{step3.title}</span> {step3.highlight}
          </h3>
          <div className="wrapper__subtitle centered">{step3.subtitle}</div>

          <div className="row">
            <div className="col">
              <div className="note-card">
                <img className="note-card__icon" alt="" src={step3.note.image} />
                <div className="note-card__content centered">{step3.note.label}</div>
                <ul className="note-card__list">
                  {step3.note.tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="col">
              <div className="card">
                <p className="card__title h4">{step3.example.title}</p>
                <p>{step3.example.context}</p>
                <p><em>{step3.example.quote}</em></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default JTBDexercise;